// TahajjudTimeCalculator.js
import React, { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import SharedHelmet from "../Helmet";
const TITLE = "Kalkulator Waktu Salat Tahajud - Hitung Sepertiga Malam Terakhir";
const DESCRIPTION =
    "Kalkulator Waktu Salat Tahajud adalah alat yang membantu Anda menentukan waktu terbaik untuk melaksanakan salat tahajud, yaitu sepertiga malam terakhir. Masukkan waktu salat Maghrib dan waktu salat Subuh untuk menghitung awal dan akhir waktu yang penuh berkah ini.";
const KEYWORDS =
    "kalkulator salat tahajud, waktu salat tahajud, hitung sepertiga malam terakhir, cara menghitung waktu tahajud, waktu terbaik salat tahajud, kalkulator qiyamul lail";
const OG_URL = "https://do-calculate.com/calculator/id/tahajjud-time/";
function TimeInput({
    label,
    hour,
    setHour,
    minute,
    setMinute,
    period,
    setPeriod,
    timeFormat,
}) {
    const hours12 = Array.from({ length: 12 }, (_, i) => i + 1);
    const hours24 = Array.from({ length: 24 }, (_, i) =>
        i.toString().padStart(2, "0")
    );
    const minutes = Array.from({ length: 60 }, (_, i) =>
        i.toString().padStart(2, "0")
    );
    return (
        <div className="form-group mb-3">
            <b>
                <label className="form-label d-block mb-2">{label}</label>
            </b>
            <div className="d-flex align-items-center">
                <select
                    value={hour}
                    onChange={(e) => setHour(e.target.value)}
                    className="form-select me-2"
                    style={{ flex: 1 }}
                >
                    {timeFormat === "12"
                        ? hours12.map((h) => (
                            <option key={h} value={h}>
                                {h}
                            </option>
                        ))
                        : hours24.map((h) => (
                            <option key={h} value={h}>
                                {h}
                            </option>
                        ))}
                </select>
                <span className="mx-1">:</span>
                <select
                    value={minute}
                    onChange={(e) => setMinute(e.target.value)}
                    className="form-select mx-2"
                    style={{ flex: 1 }}
                >
                    {minutes.map((m) => (
                        <option key={m} value={m}>
                            {m}
                        </option>
                    ))}
                </select>
                {timeFormat === "12" && (
                    <select
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        className="form-select"
                        style={{ flex: 1 }}
                    >
                        <option value="AM">Pagi</option>
                        <option value="PM">Malam</option>
                    </select>
                )}
            </div>
        </div>
    );
}
function TahajjudCalculator() {
    const [timeFormat, setTimeFormat] = useState("12");
    const [maghribHour, setMaghribHour] = useState("6");
    const [maghribMinute, setMaghribMinute] = useState("30");
    const [maghribPeriod, setMaghribPeriod] = useState("PM");
    const [fajrHour, setFajrHour] = useState("4");
    const [fajrMinute, setFajrMinute] = useState("00");
    const [fajrPeriod, setFajrPeriod] = useState("AM");
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const calculateTahajjudTime = () => {
        setIsLoading(true);
        setError(null);
        setResult(null);
        const get24HourFormat = (hourStr, minuteStr, period) => {
            let hour = parseInt(hourStr, 10);
            if (timeFormat === "12") {
                if (period === "PM" && hour !== 12) {
                    hour += 12;
                }
                if (period === "AM" && hour === 12) {
                    hour = 0;
                }
            }
            return `${hour.toString().padStart(2, "0")}:${minuteStr}`;
        };
        const maghribTime24 = get24HourFormat(
            maghribHour,
            maghribMinute,
            maghribPeriod
        );
        const fajrTime24 = get24HourFormat(fajrHour, fajrMinute, fajrPeriod);
        const maghrib = new Date(`2024-01-01T${maghribTime24}:00`);
        const fajr = new Date(`2024-01-02T${fajrTime24}:00`);
        if (fajr <= maghrib) {
            setError("Waktu Subuh harus setelah waktu Maghrib.");
            setIsLoading(false);
            return;
        }
        const nightDurationInMs = fajr - maghrib;
        const oneThirdOfNightInMs = nightDurationInMs / 3;
        const lastThirdStartTime = new Date(fajr.getTime() - oneThirdOfNightInMs);
        const formatTimeForDisplay = (date) => {
            return date.toLocaleTimeString("id-ID", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            });
        };
        setResult({
            start: formatTimeForDisplay(lastThirdStartTime),
            end: formatTimeForDisplay(fajr),
        });
        setIsLoading(false);
    };
    return (
        <div >
            <SharedHelmet
                TITLE={TITLE}
                DESCRIPTION={DESCRIPTION}
                KEYWORDS={KEYWORDS}
                OG_URL={OG_URL}
            />
            <div className="row">
                <div className="col-12 col-md-8">
                    <div
                        className="bg-success text-white text-center"
                        style={{ height: "50px", lineHeight: "50px" }}
                    >
                        <h1 style={{ fontSize: "17px" }}>Kalkulator Waktu Salat Tahajud</h1>
                    </div>
                    <div className="border border-light p-3">
                        <p>
                            Kalkulator sepertiga malam terakhir membantu Anda mengetahui waktu terbaik untuk melaksanakan salat tahajud, yaitu salah satu waktu ibadah dan doa yang paling mustajab. Alat ini menghitung waktu antara azan Maghrib dan azan Subuh untuk menentukan awal dan akhir sepertiga malam terakhir secara akurat. Cukup masukkan waktu azan Maghrib dan azan Subuh sesuai daerah Anda untuk mendapatkan hasil instan. Kalkulator ini berguna bagi umat Muslim yang mencari cara mudah mengetahui waktu qiyamul lail, terutama selama Ramadhan atau hari-hari utama.
                        </p>
                        <hr />
                        <div className="row">
                            <div className="col-sm-12 col-md-8 calculatorStyle">
                                <div className="p-3 mb-3 bg-success text-white">
                                    Masukkan Waktu Salat
                                </div>
                                <div className="d-flex justify-content-center mb-3">
                                    <div className="btn-group" role="group">
                                        <button
                                            type="button"
                                            className={`btn ${timeFormat === "12" ? "btn-success" : "btn-outline-success"
                                                }`}
                                            onClick={() => setTimeFormat("12")}
                                        >
                                            Format 12 Jam
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn ${timeFormat === "24" ? "btn-success" : "btn-outline-success"
                                                }`}
                                            onClick={() => setTimeFormat("24")}
                                        >
                                            Format 24 Jam
                                        </button>
                                    </div>
                                </div>
                                <TimeInput
                                    label="Waktu Salat Maghrib"
                                    hour={maghribHour}
                                    setHour={setMaghribHour}
                                    minute={maghribMinute}
                                    setMinute={setMaghribMinute}
                                    period={maghribPeriod}
                                    setPeriod={setMaghribPeriod}
                                    timeFormat={timeFormat}
                                />
                                <TimeInput
                                    label="Waktu Salat Subuh"
                                    hour={fajrHour}
                                    setHour={setFajrHour}
                                    minute={fajrMinute}
                                    setMinute={setFajrMinute}
                                    period={fajrPeriod}
                                    setPeriod={setFajrPeriod}
                                    timeFormat={timeFormat}
                                />
                                <hr />
                                <button
                                    className="text-white bg-success w-100"
                                    style={{ fontSize: "20px" }}
                                    onClick={calculateTahajjudTime}
                                    disabled={isLoading}
                                >
                                    {isLoading ? "Menghitung..." : "Hitung"}
                                </button>
                            </div>
                            <div className="col-sm-12 col-md-6">
                                <div className="p-3 mb-3 bg-success text-white">Hasil</div>
                                {error && <div className="alert alert-danger"><b>Kesalahan:</b> {error}</div>}
                                {result && (
                                    <div className="alert alert-success text-center">
                                        <h5>
                                            Sepertiga malam terakhir dimulai
                                            <br />
                                            (Waktu terbaik untuk tahajud)
                                        </h5>
                                        <hr />
                                        <p className="fs-5">
                                            Dari jam: <b>{result.start}</b>
                                        </p>
                                        <p className="fs-5">
                                            Sampai jam: <b>{result.end}</b> (Azan Subuh)
                                        </p>
                                    </div>
                                )}
                                {!result && !error && (
                                    <div className="alert alert-info">
                                        Hasil akan muncul di sini setelah memasukkan waktu dan menekan tombol "Hitung".
                                    </div>
                                )}
                            </div>
                        </div>
                        <hr />
                        <div className="row">
                            <RelatedLinks />
                            <hr />
                        </div>
                    </div>
                </div>
                <div className="col-sm-12 col-md-4 mx-auto">
                    <MainCalculatorsList />
                </div>
            </div>
            <br />
            <div className="row">
                <div className="col-12 col-md-8" >
                    <div className="info-sections-container">
                        <div className="info-section">
                            <div className="info-section-header">
                                Cara Menghitung Waktu Salat Tahajud
                            </div>
                            <div className="info-section-body">
                                <u>
                                    <b> Menghitung Sepertiga Malam Terakhir </b>
                                </u>
                                <br/>                                <p>
                                    Salat tahajud adalah salah satu salat sunnah yang sangat dianjurkan yang dilakukan pada malam hari. Waktu terbaik untuk melaksanakannya adalah pada sepertiga malam terakhir, ketika Allah SWT turun ke langit dunia.
                                </p>
                                <p>Untuk menghitung waktu yang penuh berkah ini, kalkulator mengikuti langkah-langkah berikut:</p>
                                <ol>
                                    <li>
                                        <b>Menentukan durasi malam:</b> Total durasi dalam menit antara waktu azan Maghrib dan waktu azan Subuh dihitung.
                                    </li>
                                    <li>
                                        <b>Membagi malam:</b> Durasi ini dibagi menjadi tiga bagian yang sama untuk menentukan durasi setiap sepertiga malam.
                                    </li>
                                    <li>
                                        <b>Menentukan sepertiga terakhir:</b> Sepertiga malam terakhir dimulai dengan mengurangi durasi satu sepertiga dari waktu Subuh, dan berlangsung hingga azan Subuh.
                                    </li>
                                </ol>
                                <p>
                                    <b>Contoh:</b> Jika Maghrib jam 6:00 malam dan Subuh jam 4:30 pagi, maka durasi malam adalah 10 jam 30 menit (630 menit). Durasi satu sepertiga adalah 3 jam 30 menit (210 menit). Dengan mengurangi durasi ini dari waktu Subuh (4:30 pagi), kita menemukan bahwa sepertiga terakhir dimulai jam 1:00 pagi.
                                </p>
                                <br />
                                <hr />
                                <b>Tanggal revisi terakhir : {new Date().toLocaleDateString('id-ID')}</b>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <br />
        </div>
    );
}
export default TahajjudCalculator;